/**
 * QueueListPage — admin seller-applications queue (EPIC-006 Phase 5, Task 5.2).
 *
 * Displays all seller applications oldest-first with:
 *   - Status multi-select filter (default: submitted + in_review)
 *   - Source filter
 *   - "Awaiting re-upload" quick-filter (client-derived)
 *   - Counts bar (Submitted / In review / Approved today / Rejected today)
 *   - Infinite scroll / "Load more" (cursor-paginated via useSellerApplications)
 *   - 30-second auto-refetch
 *   - Responsive: table on desktop, card stack on <640px
 *
 * Clicking a row opens the DetailDrawer (Task 5.3).
 *
 * Tokens: `--g-*` only. ≥44px tap targets. prefers-reduced-motion-safe.
 */
import { useState, useCallback, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { GPill, Skeleton } from "@garden/ui";
import { registerAdminI18n, ADMIN_NS } from "@/features/admin/api";
import {
  DEFAULT_FILTERS,
  isAwaitingReupload,
  useQueueCounts,
  useSellerApplications,
  type ApplicationFilters,
} from "@/features/admin/hooks/useSellerApplications";
import type { SellerApplicationListItem } from "@/features/admin/api";
import { DetailDrawer } from "@/features/admin/seller-applications/DetailDrawer";

// ─────────────────────────── Status options ────────────────────────────────

const ALL_STATUSES = ["submitted", "in_review", "approved", "rejected"];

// MUST stay equal to the API's frozen enum, `SELLER_APPLICATION_SOURCE_VALUES`
// in garden/marketplace/models.py — Postgres enforces the same two values as a
// CHECK constraint. This list previously read ["web_form", "bulk_import",
// "api"]: two chips named sources the server has never accepted, so clicking
// them 422'd (`seller_application.source_invalid`) and the queue rendered
// "Failed to load data." (SCRUM-231).
//
// Guarded by apps/api/tests/features/seller_applications/
// test_source_filter_contract.py, which fails if this literal and the API enum
// ever disagree again. Once @garden/shared-types' OpenAPI codegen lands (it is
// still the STORY-000-001 stub), import the generated union instead and drop
// both the literal and the guard.
const ALL_SOURCES = ["self_register", "bulk_import"];

// ─────────────────────────── Badge helpers ─────────────────────────────────

function statusTone(
  status: string,
  awaitingReupload: boolean,
): "warn" | "success" | "error" | "neutral" {
  if (awaitingReupload) return "warn";
  switch (status) {
    case "submitted":
      return "neutral";
    case "in_review":
      return "warn";
    case "approved":
      return "success";
    case "rejected":
      return "error";
    default:
      return "neutral";
  }
}

function sourceTone(source: string): "neutral" | "warn" {
  return source === "bulk_import" ? "warn" : "neutral";
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ─────────────────────────── CountsBar ─────────────────────────────────────

function CountsBar(): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const { data: counts, isLoading } = useQueueCounts();

  const tiles: Array<{ key: string; label: string; value: number }> = [
    { key: "submitted", label: t("queue.counts.submitted"), value: counts?.submitted ?? 0 },
    { key: "inReview", label: t("queue.counts.inReview"), value: counts?.in_review ?? 0 },
    { key: "approvedToday", label: t("queue.counts.approvedToday"), value: counts?.approved_today ?? 0 },
    { key: "rejectedToday", label: t("queue.counts.rejectedToday"), value: counts?.rejected_today ?? 0 },
  ];

  return (
    <div
      data-testid="queue-counts-bar"
      className="grid grid-cols-2 gap-2 sm:grid-cols-4"
      aria-label="Queue counts"
    >
      {tiles.map(({ key, label, value }) => (
        <div
          key={key}
          className="flex flex-col items-center rounded-[var(--g-radius-md)] bg-[var(--g-elevated)] px-3 py-2 text-center"
        >
          {isLoading ? (
            <Skeleton className="h-7 w-10" />
          ) : (
            <span className="text-2xl font-bold text-[var(--g-primary)]">
              {value}
            </span>
          )}
          <span className="mt-0.5 text-xs text-[var(--g-secondary)]">{label}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────── FilterBar ─────────────────────────────────────

interface FilterBarProps {
  filters: ApplicationFilters;
  onChange: (filters: ApplicationFilters) => void;
}

function FilterBar({ filters, onChange }: FilterBarProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);

  const toggleStatus = (status: string): void => {
    const next = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status];
    onChange({ ...filters, statuses: next });
  };

  const setSource = (source: string | null): void => {
    onChange({ ...filters, source });
  };

  const toggleAwaitingReupload = (): void => {
    onChange({ ...filters, awaitingReupload: !filters.awaitingReupload });
  };

  const clearAll = (): void => {
    onChange(DEFAULT_FILTERS);
  };

  return (
    <div data-testid="queue-filter-bar" className="flex flex-wrap items-center gap-2">
      {/* Status chips */}
      <span className="text-sm font-medium text-[var(--g-secondary)]">
        {t("queue.filter.status")}:
      </span>
      {ALL_STATUSES.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => toggleStatus(s)}
          aria-pressed={filters.statuses.includes(s)}
          className={[
            "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
            filters.statuses.includes(s)
              ? "bg-[var(--g-primary)] text-white"
              : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
          ].join(" ")}
        >
          {t(`queue.status.${s}`)}
        </button>
      ))}

      {/* Separator */}
      <span className="mx-1 text-[var(--g-hairline)]">|</span>

      {/* Source filter */}
      <span className="text-sm font-medium text-[var(--g-secondary)]">
        {t("queue.filter.source")}:
      </span>
      <button
        type="button"
        onClick={() => setSource(null)}
        aria-pressed={filters.source === null}
        className={[
          "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
          filters.source === null
            ? "bg-[var(--g-primary)] text-white"
            : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
        ].join(" ")}
      >
        All
      </button>
      {ALL_SOURCES.map((src) => (
        <button
          key={src}
          type="button"
          onClick={() => setSource(src)}
          aria-pressed={filters.source === src}
          className={[
            "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
            filters.source === src
              ? "bg-[var(--g-primary)] text-white"
              : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
          ].join(" ")}
        >
          {t(`queue.source.${src}`)}
        </button>
      ))}

      {/* Separator */}
      <span className="mx-1 text-[var(--g-hairline)]">|</span>

      {/* Awaiting re-upload quick-filter */}
      <button
        type="button"
        onClick={toggleAwaitingReupload}
        aria-pressed={filters.awaitingReupload}
        data-testid="filter-awaiting-reupload"
        className={[
          "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
          filters.awaitingReupload
            ? "bg-[var(--g-warn)] text-white"
            : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
        ].join(" ")}
      >
        {t("queue.filter.awaitingReupload")}
      </button>

      {/* Clear all */}
      <button
        type="button"
        onClick={clearAll}
        className="ml-auto min-h-[44px] rounded-full px-3 py-1 text-sm text-[var(--g-secondary)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
      >
        {t("queue.filter.clearAll")}
      </button>
    </div>
  );
}

// ─────────────────────────── Row cells ─────────────────────────────────────

interface ApplicationRowProps {
  item: SellerApplicationListItem;
  onClick: (id: string) => void;
}

function ApplicationRow({ item, onClick }: ApplicationRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const awaiting = isAwaitingReupload(item);
  const displayStatus = awaiting ? "awaiting_reupload" : item.status;

  return (
    <tr
      data-testid={`app-row-${item.id}`}
      onClick={() => onClick(item.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(item.id);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Application from ${item.business_name}`}
      className="cursor-pointer border-b border-[var(--g-hairline)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--g-primary)]"
    >
      <td className="min-h-[44px] px-3 py-3 text-sm text-[var(--g-secondary)]">
        {formatDate(item.submitted_at)}
      </td>
      <td className="px-3 py-3 text-sm font-medium">{item.business_name}</td>
      <td className="px-3 py-3 text-sm text-[var(--g-secondary)]">
        {item.market_slug}
      </td>
      <td className="px-3 py-3 text-sm text-[var(--g-secondary)]">
        {item.pavilion_number}
      </td>
      <td className="px-3 py-3">
        <GPill tone={sourceTone(item.source)} size="sm">
          {t(`queue.source.${item.source}`, { defaultValue: item.source })}
        </GPill>
      </td>
      <td className="px-3 py-3">
        <GPill tone={statusTone(item.status, awaiting)} size="sm">
          {t(`queue.status.${displayStatus}`, { defaultValue: displayStatus })}
        </GPill>
      </td>
    </tr>
  );
}

function ApplicationCard({ item, onClick }: ApplicationRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const awaiting = isAwaitingReupload(item);
  const displayStatus = awaiting ? "awaiting_reupload" : item.status;

  return (
    <div
      data-testid={`app-card-${item.id}`}
      onClick={() => onClick(item.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(item.id);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Application from ${item.business_name}`}
      className="cursor-pointer rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4 shadow-[var(--g-shadow-sm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium">{item.business_name}</div>
          <div className="mt-0.5 text-sm text-[var(--g-secondary)]">
            {item.market_slug} · {item.pavilion_number}
          </div>
          <div className="mt-1 text-xs text-[var(--g-secondary)]">
            {formatDate(item.submitted_at)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <GPill tone={statusTone(item.status, awaiting)} size="sm">
            {t(`queue.status.${displayStatus}`, { defaultValue: displayStatus })}
          </GPill>
          <GPill tone={sourceTone(item.source)} size="sm">
            {t(`queue.source.${item.source}`, { defaultValue: item.source })}
          </GPill>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Page ──────────────────────────────────────────

export function QueueListPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const [filters, setFilters] = useState<ApplicationFilters>(DEFAULT_FILTERS);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
  } = useSellerApplications(filters);

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  // Flatten pages
  const allItems: SellerApplicationListItem[] =
    data?.pages.flatMap((p) => p.items) ?? [];

  // Client-side awaiting-reupload filter (applied on top of server-side)
  const visibleItems = filters.awaitingReupload
    ? allItems.filter(isAwaitingReupload)
    : allItems;

  return (
    <div data-testid="queue-list-page" className="flex flex-col gap-4">
      {/* Page header */}
      <h1 className="text-xl font-bold">{t("queue.title")}</h1>

      {/* Counts bar */}
      <CountsBar />

      {/* Filters */}
      <FilterBar filters={filters} onChange={setFilters} />

      {/* Table — desktop (≥640px) */}
      {isLoading ? (
        <div className="hidden sm:block">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="mb-2 h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-[var(--g-error)]">
          {t("error.loadFailed")}
        </p>
      ) : visibleItems.length === 0 ? (
        <p
          data-testid="queue-empty"
          className="py-8 text-center text-sm text-[var(--g-secondary)]"
        >
          {t("queue.empty")}
        </p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-[var(--g-radius-md)] border border-[var(--g-hairline)]">
            <table
              data-testid="queue-table"
              className="w-full min-w-[600px] border-collapse bg-[var(--g-surface)] text-sm"
            >
              <thead className="bg-[var(--g-elevated)]">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("queue.columns.submittedAt")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("queue.columns.businessName")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("queue.columns.marketName")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("queue.columns.pavilionNumber")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("queue.columns.source")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("queue.columns.status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => (
                  <ApplicationRow
                    key={item.id}
                    item={item}
                    onClick={handleSelect}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card stack */}
          <div className="flex flex-col gap-2 sm:hidden" data-testid="queue-card-stack">
            {visibleItems.map((item) => (
              <ApplicationCard
                key={item.id}
                item={item}
                onClick={handleSelect}
              />
            ))}
          </div>

          {/* Load more */}
          {hasNextPage ? (
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="mx-auto min-h-[44px] rounded-full bg-[var(--g-elevated)] px-6 py-2 text-sm font-medium text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-50"
            >
              {isFetchingNextPage ? t("queue.refreshing") : t("queue.loadMore")}
            </button>
          ) : null}
        </>
      )}
      {/* Detail drawer — self-contained; applicationId=null means closed */}
      <DetailDrawer
        applicationId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

export default QueueListPage;
